import React, { createContext, useContext, useReducer, useEffect } from 'react'
import PropTypes from 'prop-types'

const AdminContext = createContext()

const initialState = {
  // Admin authentication
  isAdmin: undefined, // undefined means not checked yet
  adminUser: null,
  
  // Dashboard data
  dashboard: {
    stats: {
      totalSales: 0,
      totalOrders: 0,
      totalProducts: 0,
      totalUsers: 0,
    },
    recentOrders: [],
    topProducts: [],
    salesChart: [],
  },
  
  // Admin permissions
  permissions: {
    products: false,
    orders: false,
    users: false,
    categories: false,
    reports: false,
    settings: false,
  },
  
  // Current admin view
  currentView: 'dashboard',
  
  // Admin UI state
  ui: {
    sidebarCollapsed: false,
    showNotifications: true,
    theme: 'light',
  },
}

const adminReducer = (state, action) => {
  switch (action.type) {
    case 'SET_ADMIN_AUTH':
      return {
        ...state,
        isAdmin: action.payload.isAdmin,
        adminUser: action.payload.adminUser,
        permissions: action.payload.permissions || state.permissions,
      }
    
    case 'SET_DASHBOARD_DATA':
      return {
        ...state,
        dashboard: {
          ...state.dashboard,
          ...action.payload,
        },
      }
    
    case 'SET_CURRENT_VIEW':
      return {
        ...state,
        currentView: action.payload,
      }
    
    case 'TOGGLE_SIDEBAR':
      return {
        ...state,
        ui: {
          ...state.ui,
          sidebarCollapsed: !state.ui.sidebarCollapsed,
        },
      }
    
    case 'UPDATE_UI_SETTING':
      return {
        ...state,
        ui: {
          ...state.ui,
          [action.payload.key]: action.payload.value,
        },
      }
    
    case 'LOGOUT_ADMIN':
      return {
        ...initialState,
        isAdmin: false, // Explicitly set to false
        ui: state.ui, // Keep UI preferences
      }
    
    default:
      return state
  }
}

export const AdminProvider = ({ children }) => {
  const [state, dispatch] = useReducer(adminReducer, initialState)

  // Initialize admin auth on provider mount
  useEffect(() => {
    // For testing, automatically set admin as authenticated
    const adminData = {
      id: 1,
      username: 'Saeed',
      name: 'Saeed',
      email: 'saeed@maurifood.com',
      role: 'admin',
      permissions: {
        products: true,
        orders: true,
        users: true,
        categories: true,
        reports: true,
        settings: true,
      }
    }
    
    // Set admin as authenticated immediately
    dispatch({ 
      type: 'SET_ADMIN_AUTH', 
      payload: { isAdmin: true, adminUser: adminData } 
    })
  }, [])

  const setAdminAuth = (authData) => {
    dispatch({ type: 'SET_ADMIN_AUTH', payload: authData })
  }

  const setDashboardData = (data) => {
    dispatch({ type: 'SET_DASHBOARD_DATA', payload: data })
  }

  const setCurrentView = (view) => {
    dispatch({ type: 'SET_CURRENT_VIEW', payload: view })
  }

  const toggleSidebar = () => {
    dispatch({ type: 'TOGGLE_SIDEBAR' })
  }

  const updateUISetting = (key, value) => {
    dispatch({ type: 'UPDATE_UI_SETTING', payload: { key, value } })
  }

  const logoutAdmin = () => {
    localStorage.removeItem('maurifood_admin_token')
    dispatch({ type: 'LOGOUT_ADMIN' })
  }

  const loginAdmin = async (credentials) => {
    try {
      // Check admin credentials
      if (credentials.username === 'Saeed' && credentials.password === 'M@uri321') {
        const adminData = {
          id: 1,
          username: 'Saeed',
          name: 'Saeed',
          email: 'saeed@maurifood.com',
          role: 'admin',
          permissions: {
            products: true,
            orders: true,
            users: true,
            categories: true,
            reports: true,
            settings: true,
          }
        }
        
        // Generate a simple token (in real app, this would be from server)
        const token = 'admin_token_' + Date.now()
        localStorage.setItem('maurifood_admin_token', token)
        
        dispatch({ 
          type: 'SET_ADMIN_AUTH', 
          payload: { 
            isAdmin: true, 
            adminUser: adminData 
          } 
        })
        
        return { success: true }
      } else {
        return { success: false, error: 'Invalid username or password' }
      }
    } catch (error) {
      return { success: false, error: 'Login failed' }
    }
  }

  const checkAdminAuth = () => {
    const token = localStorage.getItem('maurifood_admin_token')
    if (token) {
      // In a real app, you would verify the token with the server
      const adminData = {
        id: 1,
        username: 'Saeed',
        name: 'Saeed',
        email: 'saeed@maurifood.com',
        role: 'admin',
        permissions: {
          products: true,
          orders: true,
          users: true,
          categories: true,
          reports: true,
          settings: true,
        }
      }
      
      dispatch({ 
        type: 'SET_ADMIN_AUTH', 
        payload: { 
          isAdmin: true, 
          adminUser: adminData 
        } 
      })
    } else {
      // No token found, set as not authenticated
      dispatch({ 
        type: 'SET_ADMIN_AUTH', 
        payload: { 
          isAdmin: false, 
          adminUser: null 
        } 
      })
    }
  }

  const value = {
    ...state,
    setAdminAuth,
    setDashboardData,
    setCurrentView,
    toggleSidebar,
    updateUISetting,
    logoutAdmin,
    loginAdmin,
    checkAdminAuth,
  }

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>
}

AdminProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const useAdmin = () => {
  const context = useContext(AdminContext)
  if (!context) {
    console.warn('useAdmin must be used within an AdminProvider')
    // Return a default context to prevent crashes
    return {
      isAdmin: false,
      adminUser: null,
      loginAdmin: () => {},
      logoutAdmin: () => {},
      checkAdminAuth: () => {},
      dispatch: () => {},
      state: initialState
    }
  }
  return context
}
